import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";
import { DisclosureChevron, ExpandIn } from "@/components/common/Disclosure";
import { AgentsPage } from "./pages/AgentsPage";
import { ChatPage } from "./pages/ChatPage";
import { DevicesPage } from "./pages/DevicesPage";
import { OverviewPage } from "./pages/OverviewPage";
import { PricingPage } from "./pages/PricingPage";
import { RolesPage } from "./pages/RolesPage";
import { ACTION_LINK_CLASS } from "@/lib/link-styles";
import { DocsPageToc } from "./DocsPageToc";
import { useCurrentHeading } from "./useCurrentHeading";
import { useDocHeadings } from "./useDocHeadings";

// 全ページを常に描画するので、表示したときに取り直すページは active を見る
type DocPageEntry = {
	slug: string;
	label: string;
	Content: (props: { active: boolean }) => React.ReactElement;
};

const DOC_PAGES: DocPageEntry[] = [
	{ slug: "overview", label: "概要", Content: OverviewPage },
	{ slug: "chat", label: "チャット", Content: ChatPage },
	{ slug: "agents", label: "エージェント", Content: AgentsPage },
	{ slug: "devices", label: "ローカルデバイス", Content: DevicesPage },
	{ slug: "roles", label: "ロールと権限", Content: RolesPage },
	{ slug: "pricing", label: "料金", Content: PricingPage },
];

export function DocsPage() {
	const { page: pageParam } = useParams<{ page: string }>();
	const page = DOC_PAGES.find((p) => p.slug === pageParam);
	if (!page) return <Navigate to={`/docs/${DOC_PAGES[0].slug}`} replace />;
	return <DocsLayout page={page} />;
}

function DocsLayout({ page }: { page: DocPageEntry }) {
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
	const [headingsBySlug, setHeadingsBySlug] = useState<Record<string, HTMLHeadingElement[]>>({});
	const [openSlugs, setOpenSlugs] = useState<ReadonlySet<string>>(() => new Set([page.slug]));
	// 最初の表示で開いていたページは、閉じるまでアニメーションなしで出す
	const [initialOpenSlug, setInitialOpenSlug] = useState<string | null>(page.slug);
	const [pendingHeading, setPendingHeading] = useState<HTMLHeadingElement | null>(null);

	// ページ名以外の手段で移ったときも、移った先を同じレンダーで開いてちらつかせない
	const [prevSlug, setPrevSlug] = useState(page.slug);
	if (prevSlug !== page.slug) {
		setPrevSlug(page.slug);
		if (!openSlugs.has(page.slug)) setOpenSlugs(new Set(openSlugs).add(page.slug));
	}

	// 見出しはページ本文から拾い、ナビの目次をページごとに書かずに済ませる。各ページの h2 は静的
	useLayoutEffect(() => {
		setHeadingsBySlug(
			Object.fromEntries(
				DOC_PAGES.map((p) => [
					p.slug,
					Array.from(sectionRefs.current[p.slug]?.querySelectorAll("h2") ?? []),
				]),
			),
		);
	}, []);

	const [main, setMain] = useState<HTMLElement | null>(null);
	const [activeSection, setActiveSection] = useState<HTMLElement | null>(null);
	useLayoutEffect(() => {
		setActiveSection(sectionRefs.current[page.slug] ?? null);
	}, [page.slug]);
	const pageHeadings = useDocHeadings(activeSection);
	const [currentHeading, scrollToTarget] = useCurrentHeading(main, pageHeadings, page.slug);

	// 同じハッシュのリンクを押し直しても飛べるよう、location.key ごとにスクロールする
	const { hash, key } = useLocation();
	useEffect(() => {
		if (!hash) return;
		const target = sectionRefs.current[page.slug]?.querySelector(
			`#${CSS.escape(decodeURIComponent(hash.slice(1)))}`,
		);
		if (target) scrollToTarget(target);
	}, [hash, key, page.slug, scrollToTarget]);

	// 隠れているページの見出しは、そのページが表示されてからスクロールする
	useEffect(() => {
		if (!pendingHeading || !sectionRefs.current[page.slug]?.contains(pendingHeading)) return;
		scrollToTarget(pendingHeading);
		setPendingHeading(null);
	}, [pendingHeading, page.slug, scrollToTarget]);

	const navigate = useNavigate();
	const toggle = (slug: string) => {
		const next = new Set(openSlugs);
		if (next.delete(slug)) {
			if (slug === initialOpenSlug) setInitialOpenSlug(null);
		} else {
			next.add(slug);
		}
		setOpenSlugs(next);
	};
	const goToHeading = (slug: string, heading: HTMLHeadingElement) => {
		if (slug === page.slug) {
			scrollToTarget(heading);
			return;
		}
		setPendingHeading(heading);
		void navigate(`/docs/${slug}`);
	};

	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "200px 1fr",
				height: "100vh",
				background: "var(--surface-1)",
			}}
		>
			<nav
				style={{
					borderRight: "var(--pane-header-border)",
					padding: "12px 8px",
					display: "flex",
					flexDirection: "column",
					gap: 2,
					minHeight: 0,
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						height: 40,
						padding: "0 10px",
						marginBottom: 4,
					}}
				>
					<h1
						style={{
							margin: 0,
							fontSize: "var(--text-ui)",
							fontWeight: 600,
							letterSpacing: "var(--tracking-snug)",
							color: "var(--fg-1)",
						}}
					>
						利用ガイド
					</h1>
				</div>

				<div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
					{DOC_PAGES.map((p) => {
						const active = p.slug === page.slug;
						const open = openSlugs.has(p.slug);
						return (
							<div key={p.slug} className="flex shrink-0 flex-col gap-0.5">
								{/* 別のページなら移って開く (開くのは slug の変化を見て行う)。表示中のページなら開閉だけ */}
								<Link
									to={`/docs/${p.slug}`}
									onClick={(e) => {
										if (!active) return;
										e.preventDefault();
										toggle(p.slug);
									}}
									data-testid={`docs-nav-page-${p.slug}`}
									className={`flex h-8 shrink-0 items-center gap-1 rounded-3 pr-2.5 pl-2.5 text-ui no-underline hover:text-fg-0 ${active ? "bg-surface-2 font-semibold text-fg-1" : "text-fg-2"}`}
								>
									<DisclosureChevron open={open} size={12} />
									<span className="min-w-0 truncate">{p.label}</span>
								</Link>
								{open && (
									<ExpandIn animate={p.slug !== initialOpenSlug}>
										<div className="flex flex-col gap-0.5">
											{(headingsBySlug[p.slug] ?? []).map((heading, i) => (
												<button
													key={i}
													type="button"
													onClick={() => goToHeading(p.slug, heading)}
													title={heading.textContent ?? undefined}
													data-tooltip-if-truncated
													className="block h-7 w-full min-w-0 shrink-0 cursor-pointer truncate border-0 bg-transparent pr-2.5 pl-6.5 text-left text-list leading-7 text-fg-3 hover:text-fg-1"
												>
													{heading.textContent}
												</button>
											))}
										</div>
									</ExpandIn>
								)}
							</div>
						);
					})}
				</div>

				<Link
					to="/"
					data-testid="docs-open-app"
					className={`flex h-8 shrink-0 items-center justify-center ${ACTION_LINK_CLASS}`}
				>
					アプリに移動
				</Link>
			</nav>

			<main ref={setMain} style={{ overflow: "auto" }}>
				<div className="flex items-start gap-12 px-12 py-8">
					<article className="min-w-0 flex-1 select-text" style={{ maxWidth: 760 }}>
						{DOC_PAGES.map((p) => (
							<section
								key={p.slug}
								ref={(el) => {
									sectionRefs.current[p.slug] = el;
								}}
								hidden={p.slug !== page.slug}
							>
								<p.Content active={p.slug === page.slug} />
							</section>
						))}
					</article>
					<DocsPageToc
						headings={pageHeadings}
						currentIndex={currentHeading}
						onSelect={scrollToTarget}
					/>
				</div>
			</main>
		</div>
	);
}
